import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { HiOutlineArrowLeft } from 'react-icons/hi2';
import { subscriptionService, paymentService, authService } from '../services';
import { usePermissions } from '../hooks/usePermissions';
import PageLoading from "../components/common/PageLoading";
import './SubscriptionPurchase.css';

const SubscriptionPurchase = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission } = usePermissions();
  
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState('select'); // select, payment, success
  const [tenantId, setTenantId] = useState(null);
  const [registrationMessage, setRegistrationMessage] = useState('');
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useEffect(() => {
    // Get tenantId from location state (from registration) or localStorage
    const stateTenantId = location.state?.tenantId;
    const storedTenantId = localStorage.getItem('pendingTenantId');
    const finalTenantId = stateTenantId || (storedTenantId ? parseInt(storedTenantId, 10) : null);
    
    // Also check if user is already logged in
    const loggedInTenantId = authService.getTenantId();
    const userIsLoggedIn = !!authService.getToken();
    setIsLoggedIn(userIsLoggedIn);
    
    if (finalTenantId) {
      setTenantId(finalTenantId);
      if (location.state?.message) {
        setRegistrationMessage(location.state.message);
      }
    } else if (loggedInTenantId) {
      setTenantId(loggedInTenantId);
    } else {
      // No tenantId - redirect to registration
      navigate('/register', { 
        state: { 
          message: 'Please register first to purchase a subscription plan' 
        } 
      });
      return;
    }

    // Only check permissions if user is logged in (not coming from registration)
    // During registration flow, allow plan selection without permission checks
    if (userIsLoggedIn) {
      const canView = hasPermission('Payment.View');
      if (!canView) {
        setError('You do not have permission to view payment information');
        setLoading(false);
        return;
      }
    }

    loadPlans();
  }, [location, navigate, hasPermission]);

  const loadPlans = async () => {
    try {
      setLoading(true);
      const data = await subscriptionService.getPlans();
      setPlans(data);
    } catch (err) {
      setError('Failed to load subscription plans. Please try again.');
      console.error('Error loading plans:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPlan = (plan) => {
    // Only check permissions if user is logged in
    // During registration flow, allow plan selection without permission checks
    if (isLoggedIn) {
      const canView = hasPermission('Payment.View');
      if (!canView) {
        setError('You do not have permission to view payment information');
        return;
      }
    }
    setSelectedPlan(plan);
    setStep('payment');
    setError('');
  };

  const handlePurchase = async () => {
    if (!selectedPlan || !tenantId) return;

    // Only check permissions if user is logged in
    // During registration flow, allow purchase without permission checks
    if (isLoggedIn) {
      const canManage = hasPermission('Payment.Manage');
      if (!canManage) {
        setError('You do not have permission to manage payments');
        return;
      }
    }

    try {
      setPurchasing(true);
      setError('');

      // If free plan, purchase directly
      if (selectedPlan.price === 0) {
        await subscriptionService.purchaseSubscription(tenantId, selectedPlan.planId);
        setStep('success');
      } else {
        // For paid plans, initiate payment
        const paymentData = {
          tenantId: tenantId,
          planId: selectedPlan.planId,
          amount: selectedPlan.price,
          currency: selectedPlan.currency || 'INR',
        };

        const paymentResponse = await paymentService.initiatePayment(paymentData);
        
        if (paymentResponse.paymentUrl) {
          // Redirect to payment gateway
          window.location.href = paymentResponse.paymentUrl;
        } else {
          setError('Failed to initiate payment. Please try again.');
        }
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
        err.message ||
        'Failed to process purchase. Please try again.'
      );
    } finally {
      setPurchasing(false);
    }
  };

  const handleBack = () => {
    setStep('select');
    setSelectedPlan(null);
    setError('');
  };

  const handleSuccessContinue = () => {
    // Clear pending tenantId from localStorage
    localStorage.removeItem('pendingTenantId');
    // Redirect to login page
    const pendingUsername = localStorage.getItem('pendingUsername');
    navigate('/login', { 
      state: { 
        message: 'Subscription activated successfully! Please login to continue.',
        tenantId: tenantId,
        username: pendingUsername 
      } 
    });
  };

  if (loading) {
    return (
      <div className="subscription-purchase-page">
        <PageLoading message="Loading subscription plans..." />
      </div>
    );
  }

  // Check if user is logged in and has permission (only for logged-in users)
  if (isLoggedIn) {
    const canView = hasPermission('Payment.View');
    if (!canView) {
      return (
        <div className="subscription-purchase-page">
          <div className="error-message" style={{ 
            textAlign: 'center', 
            padding: '2rem',
            fontSize: '1.1rem',
            color: '#d93025'
          }}>
            <h2>Access Denied</h2>
            <p>You do not have permission to view payment information</p>
          </div>
        </div>
      );
    }
  }

  return (
    <div className="subscription-purchase-page">
      <div className="purchase-container">
            {step === 'select' && (
              <div className="purchase-step">
                <div className="purchase-header">
                  <h1 className="page-title">Choose Your Plan</h1>
                  <p className="page-subtitle">Select a subscription plan that fits your needs</p>
                </div>

            {registrationMessage && (
              <div className="info-message">
                {registrationMessage}
              </div>
            )}

            {error && <div className="error-message">{error}</div>}

            <div className="plans-grid">
              {plans.map((plan) => (
                <div
                  key={plan.planId}
                  className={`plan-card ${plan.planCode === 'PROFESSIONAL' ? 'highlighted' : ''}`}
                >
                  {plan.planCode === 'PROFESSIONAL' && (
                    <div className="popular-badge">Most Popular</div>
                  )}
                  <div className="plan-header">
                    <h3 className="plan-name">{plan.planName}</h3>
                    <div className="plan-price">
                      <span className="price-amount">
                        {plan.price === 0 ? 'Free' : `$${plan.price}`}
                      </span>
                      <span className="price-period">/{plan.billingCycle?.toLowerCase() || 'month'}</span>
                    </div>
                    <p className="plan-description">{plan.description}</p>
                  </div>
                  <ul className="plan-features">
                    {plan.maxPlants > 0 && (
                      <li>
                        <span className="check-icon">✓</span>
                        {plan.maxPlants === 0 ? 'Unlimited' : plan.maxPlants} Plants
                      </li>
                    )}
                    {plan.maxUsers > 0 && (
                      <li>
                        <span className="check-icon">✓</span>
                        {plan.maxUsers === 0 ? 'Unlimited' : plan.maxUsers} Users
                      </li>
                    )}
                    {plan.maxMachines > 0 && (
                      <li>
                        <span className="check-icon">✓</span>
                        {plan.maxMachines === 0 ? 'Unlimited' : plan.maxMachines} Machines
                      </li>
                    )}
                    {plan.maxStorageGB > 0 && (
                      <li>
                        <span className="check-icon">✓</span>
                        {plan.maxStorageGB === 0 ? 'Unlimited' : plan.maxStorageGB}GB Storage
                      </li>
                    )}
                  </ul>
                  <button
                    className="plan-button"
                    onClick={() => handleSelectPlan(plan)}
                    disabled={isLoggedIn && !hasPermission('Payment.View')}
                  >
                    Select Plan
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

            {step === 'payment' && selectedPlan && (
              <div className="purchase-step">
                <button className="back-button" onClick={handleBack}>
                  <HiOutlineArrowLeft className="back-icon" />
                  Back to Plans
                </button>
                <div className="purchase-header">
                  <h1 className="page-title">Confirm Purchase</h1>
                  <p className="page-subtitle">Review your selected plan</p>
                </div>

            {error && <div className="error-message">{error}</div>}

            <div className="payment-summary">
              <div className="summary-card">
                <h3>Selected Plan</h3>
                <div className="summary-details">
                  <div className="summary-row">
                    <span>Plan:</span>
                    <strong>{selectedPlan.planName}</strong>
                  </div>
                  <div className="summary-row">
                    <span>Billing Cycle:</span>
                    <strong>{selectedPlan.billingCycle}</strong>
                  </div>
                  <div className="summary-row">
                    <span>Price:</span>
                    <strong className="price-highlight">
                      {selectedPlan.price === 0 ? 'Free' : `$${selectedPlan.price}`}
                    </strong>
                  </div>
                </div>
              </div>

              {selectedPlan.price > 0 && (
                <div className="payment-note">
                  <p>You will be redirected to PhonePe payment gateway to complete the purchase.</p>
                </div>
              )}

              <div className="payment-actions">
                <button
                  className="btn-secondary"
                  onClick={handleBack}
                  disabled={purchasing}
                >
                  Cancel
                </button>
                <button
                  className="btn-primary"
                  onClick={handlePurchase}
                  disabled={purchasing || (isLoggedIn && !hasPermission('Payment.Manage'))}
                >
                  {purchasing ? 'Processing...' : selectedPlan.price === 0 ? 'Activate Free Plan' : 'Proceed to Payment'}
                </button>
              </div>
            </div>
          </div>
        )}

            {step === 'success' && (
              <div className="purchase-step">
                <div className="success-container">
                  <div className="success-icon">✓</div>
                  <h1 className="page-title">Subscription Activated!</h1>
                  <p className="page-subtitle">
                    Your {selectedPlan?.planName} plan has been successfully activated.
                  </p>
                  <button className="btn-primary" onClick={handleSuccessContinue}>
                    Go to Dashboard
                  </button>
                </div>
              </div>
            )}
          </div>
    </div>
  );
};

export default SubscriptionPurchase;

